/**
 * NotificationsTab — notification/email admin console.
 * Extracted verbatim from AdminScreen.jsx (god-file decomposition).
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Platform } from 'react-native';
import { db } from '../firebaseConfig';
import { collection, doc, onSnapshot, orderBy, query, updateDoc, serverTimestamp } from 'firebase/firestore';
import { adminStyles as styles } from './adminStyles';

export default function NotificationsTab() {
  const [notifications, setNotifications] = useState([]);
  const [watchers, setWatchers] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'read'
  const [expandedNotifId, setExpandedNotifId] = useState(null);

  // Load notifications
  useEffect(() => {
    const q = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setNotifications(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Load query watchers
  useEffect(() => {
    const q = query(collection(db, 'query_watchers'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setWatchers(list);
    });
    return () => unsub();
  }, []);

  // Load user data
  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsub = onSnapshot(q, (snap) => {
      const userMap = {};
      snap.forEach((d) => {
        const data = d.data();
        userMap[d.id] = {
          email: data.email || d.id,
          displayName: data.displayName || null
        };
      });
      setUsers(userMap);
    });
    return () => unsub();
  }, []);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  const stats = {
    total: notifications.length,
    unread: notifications.filter(n => !n.read).length,
    read: notifications.filter(n => n.read).length,
    activeWatchers: watchers.filter(w => w.status === 'active').length,
    completedWatchers: watchers.filter(w => w.status === 'completed').length,
    expiredWatchers: watchers.filter(w => w.status === 'expired').length,
  };

  const deleteNotification = async (id) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { deleted: true, updatedAt: serverTimestamp() });
    } catch (e) {
      console.error('Failed to delete notification', e);
    }
  };

  const deleteWatcher = async (id) => {
    try {
      await updateDoc(doc(db, 'query_watchers', id), { status: 'cancelled', updatedAt: serverTimestamp() });
    } catch (e) {
      console.error('Failed to delete watcher', e);
    }
  };

  return (
    <View style={styles.tabWrap}>
      <Text style={styles.sectionTitle}>Notifications & Watchers {loading ? '(loading...)' : `(${notifications.length} notifications, ${watchers.length} watchers)`}</Text>
      
      {/* Stats */}
      <View style={[styles.card, { marginBottom: 16 }]}>
        <Text style={styles.groupHeader}>Statistics</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Total Notifications</Text>
            <Text style={{ color: '#fff', fontSize: 24, fontWeight: '700' }}>{stats.total}</Text>
          </View>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Unread</Text>
            <Text style={{ color: '#fbbf24', fontSize: 24, fontWeight: '700' }}>{stats.unread}</Text>
          </View>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Read</Text>
            <Text style={{ color: '#4ade80', fontSize: 24, fontWeight: '700' }}>{stats.read}</Text>
          </View>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Active Watchers</Text>
            <Text style={{ color: '#4CAF50', fontSize: 24, fontWeight: '700' }}>{stats.activeWatchers} <Text style={{ fontSize: 14 }}>👁️</Text></Text>
          </View>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Completed</Text>
            <Text style={{ color: '#9ca3af', fontSize: 24, fontWeight: '700' }}>{stats.completedWatchers}</Text>
          </View>
          <View style={{ minWidth: 120 }}>
            <Text style={styles.caption}>Expired</Text>
            <Text style={{ color: '#f87171', fontSize: 24, fontWeight: '700' }}>{stats.expiredWatchers}</Text>
          </View>
        </View>
      </View>

      {/* Filter */}
      <View style={[styles.row, { marginBottom: 16 }]}>
        <Text style={styles.label}>Filter:</Text>
        {['all', 'unread', 'read'].map((f) => (
          <TouchableOpacity
            key={f}
            onPress={() => setFilter(f)}
            style={[styles.tag, filter === f ? styles.tagActive : null]}
          >
            <Text style={[styles.tagText, filter === f ? styles.tagTextActive : null]}>
              {f === 'all' ? 'All' : f === 'unread' ? `Unread (${stats.unread})` : `Read (${stats.read})`}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Notifications List */}
      <Text style={styles.groupHeader}>Notifications</Text>
      <ScrollView style={{ flex: 1, maxHeight: 300 }} contentContainerStyle={{ paddingVertical: 8 }}>
        {filteredNotifications.map((n) => {
          const user = users[n.uid];
          const isExpanded = expandedNotifId === n.id;
          
          return (
            <View key={n.id} style={[styles.userRow, { flexDirection: 'column', alignItems: 'stretch' }]}>
              <TouchableOpacity 
                onPress={() => setExpandedNotifId(isExpanded ? null : n.id)}
                style={{ flex: 1 }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.userEmail, !n.read && { fontWeight: '700', color: '#fbbf24' }]}>{n.title || 'No title'}</Text>
                    <Text style={[styles.userMeta, { marginTop: 4 }]}>
                      {user?.email || n.uid || 'Unknown'} · {n.createdAt?.toDate?.() ? n.createdAt.toDate().toLocaleString() : 'Unknown date'}
                    </Text>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                    {!n.read && (
                      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#fbbf24' }} />
                    )}
                    <TouchableOpacity
                      onPress={(e) => {
                        e.stopPropagation();
                        deleteNotification(n.id);
                      }}
                      style={{ padding: 4 }}
                    >
                      <Text style={{ color: '#f87171', fontSize: 16 }}>✕</Text>
                    </TouchableOpacity>
                  </View>
                </View>
                {isExpanded && (
                  <View style={{ marginTop: 8, padding: 12, backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 8 }}>
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, marginBottom: 8 }}>{n.message || 'No message'}</Text>
                    {n.fixtureTitle && (
                      <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>Fixture: {n.fixtureTitle}</Text>
                    )}
                    {n.fixtureId && (
                      <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>
                        ID: {n.fixtureId}
                      </Text>
                    )}
                  </View>
                )}
              </TouchableOpacity>
            </View>
          );
        })}
        {filteredNotifications.length === 0 && (
          <Text style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', paddingVertical: 24 }}>No notifications</Text>
        )}
      </ScrollView>

      {/* Watchers List */}
      <Text style={[styles.groupHeader, { marginTop: 24 }]}>Query Watchers</Text>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 8 }}>
        {watchers.map((w) => {
          const user = users[w.uid];
          const statusColor = w.status === 'active' ? '#4CAF50' : w.status === 'completed' ? '#9ca3af' : '#f87171';
          
          return (
            <View key={w.id} style={[styles.userRow, { borderLeftWidth: 3, borderLeftColor: statusColor }]}>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <View style={{ paddingHorizontal: 8, paddingVertical: 2, backgroundColor: `${statusColor}20`, borderRadius: 4 }}>
                    <Text style={{ color: statusColor, fontSize: 11, fontWeight: '700' }}>{w.status?.toUpperCase()}</Text>
                  </View>
                  <Text style={styles.userEmail}>{w.fixtureTitle || 'No title'}</Text>
                </View>
                <Text style={styles.userMeta}>
                  👤 {user?.email || w.uid || 'Unknown'} · {w.createdAt?.toDate?.() ? w.createdAt.toDate().toLocaleDateString() : 'Unknown'}
                </Text>
                {w.startDate && (
                  <Text style={[styles.userMeta, { marginTop: 2 }]}>
                    🎮 {new Date(w.startDate).toLocaleString()}
                  </Text>
                )}
                {w.lastChecked && (
                  <Text style={[styles.userMeta, { marginTop: 2, fontSize: 11 }]}>
                    Last checked: {w.lastChecked?.toDate?.() ? new Date(w.lastChecked.toDate()).toLocaleTimeString() : 'Unknown'}
                  </Text>
                )}
              </View>
              <TouchableOpacity
                onPress={() => deleteWatcher(w.id)}
                style={{ padding: 8, backgroundColor: 'rgba(244, 67, 54, 0.15)', borderRadius: 6 }}
              >
                <Text style={{ color: '#f87171', fontSize: 12 }}>Cancel</Text>
              </TouchableOpacity>
            </View>
          );
        })}
        {watchers.length === 0 && (
          <Text style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', paddingVertical: 24 }}>No watchers</Text>
        )}
      </ScrollView>
    </View>
  );
}

